<template>
  <div>
    <div class="regra-modal__secao">
      <div class="regra-modal__secao-titulo">Para quem a regra vale</div>
      <p class="regra-negocio-restricoes__descricao">
        Restrinja os clientes em que a regra é validada. Sem restrições, a regra vale para todos os clientes dos canais selecionados.
      </p>

      <CRow class="align-items-end">
        <CCol md="4" class="mb-2 mb-md-0">
          <label class="regra-modal__rotulo">Tipo</label>
          <SeletorBusca
            :value="nova.tipo"
            :opcoes="opcoesTipo"
            :mostrar-codigo="false"
            :clearable="false"
            placeholder="Selecione o tipo"
            aria-label="Tipo de restrição"
            @change="trocarTipo"
          />
        </CCol>
        <CCol md="5" class="mb-2 mb-md-0">
          <label class="regra-modal__rotulo">{{ rotuloCodigo }}</label>
          <SeletorBusca
            :key="nova.tipo"
            :value="nova.chave"
            :url="urlBusca"
            :mapear="mapearOpcao"
            :rotulo-atual="rotulos[chaveRotulo(nova.tipo, nova.chave)] || ''"
            :livre="nova.tipo !== 'CLIENTE'"
            :placeholder="placeholderChave"
            texto-vazio="Nenhum resultado encontrado. Digite o código e pressione Enter"
            :aria-label="rotuloCodigo"
            @change="escolherChave"
          />
        </CCol>
        <CCol md="3">
          <CButton color="primary" block :disabled="!podeAdicionar" @click="adicionar">
            <fa-icon icon="plus" class="mr-1" /> Incluir
          </CButton>
        </CCol>
      </CRow>
      <small v-if="duplicada" class="regra-modal__campo-erro">Essa restrição já foi incluída.</small>
      <small v-else-if="chaveDigitada.length > tamanhoMaximoChave" class="regra-modal__campo-erro">
        O código pode ter no máximo {{ tamanhoMaximoChave }} caracteres.
      </small>

      <div class="regra-restricoes__quadro">
        <div class="regra-restricoes__cabecalho">
          <template v-if="grupos.length">
            A regra vale para o cliente que atende <strong>todas as linhas</strong> abaixo.
            <template v-if="algumaLinhaComVarios">Dentro de uma mesma linha, basta um dos valores.</template>
          </template>
          <template v-else>Sem restrições: a regra vale para todos os clientes.</template>
        </div>

        <template v-for="(grupo, indice) in grupos">
          <div v-if="indice" :key="`conector-${grupo.tipo}`" class="regra-restricoes__conector">
            <span class="regra-restricoes__conector-texto regra-restricoes__conector-texto--e">E</span>
          </div>
          <div :key="grupo.tipo" class="regra-restricoes__linha">
            <div class="regra-restricoes__linha-tipo">{{ grupo.rotulo }}</div>
            <div class="regra-restricoes__linha-valores">
              <span v-if="grupo.restricoes.length > 1" class="regra-restricoes__linha-dica">um destes:</span>
              <span v-for="restricao in grupo.restricoes" :key="`${restricao.tipo}|${restricao.chave}`" class="regra-restricoes__valor">
                {{ descricaoDe(restricao) }}
                <button
                  type="button"
                  class="regra-restricoes__remover"
                  title="Remover"
                  :aria-label="`Remover ${grupo.rotulo} ${restricao.chave}`"
                  @click="remover(restricao)"
                >
                  &times;
                </button>
              </span>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import SeletorBusca from "@/components/form/SeletorBusca.vue";
import { TIPOS_RESTRICAO, rotuloRestricao } from "../../../constantes";
import { chaveRotulo, descricaoRestricao, mapearOpcaoRestricao, urlOpcoesRestricao } from "../../../helpers/restricoes";

const TAMANHO_MAXIMO_CHAVE = 250;

export default {
  name: "RestricoesRegraNegocio",
  components: { SeletorBusca },

  props: {
    item: { type: Object, required: true },
    rotulos: { type: Object, default: () => ({}) },
  },

  data() {
    return {
      opcoesTipo: TIPOS_RESTRICAO.map((tipo) => ({ id: tipo.chave, descricao: tipo.label })),
      nova: { tipo: TIPOS_RESTRICAO[0].chave, chave: "" },
      tamanhoMaximoChave: TAMANHO_MAXIMO_CHAVE,
    };
  },

  computed: {
    tipoAtual() {
      return TIPOS_RESTRICAO.find((tipo) => tipo.chave === this.nova.tipo) || { rotuloCodigo: "Código" };
    },

    rotuloCodigo() {
      return this.tipoAtual.rotuloCodigo;
    },

    placeholderChave() {
      if (this.nova.tipo === "CLIENTE") return "Busque pelo código, documento ou nome";

      return "Busque pelo código ou nome";
    },

    urlBusca() {
      return urlOpcoesRestricao(this.nova.tipo);
    },

    chaveDigitada() {
      return String(this.nova.chave || "").trim();
    },

    duplicada() {
      return !!this.chaveDigitada && this.item.restricoes.some((restricao) => restricao.tipo === this.nova.tipo && `${restricao.chave}` === this.chaveDigitada);
    },

    podeAdicionar() {
      return !!this.nova.tipo && !!this.chaveDigitada && this.chaveDigitada.length <= TAMANHO_MAXIMO_CHAVE && !this.duplicada;
    },

    grupos() {
      return this.item.restricoes.reduce((grupos, restricao) => {
        let grupo = grupos.find((existente) => existente.tipo === restricao.tipo);

        if (!grupo) {
          grupo = { tipo: restricao.tipo, rotulo: rotuloRestricao(restricao.tipo), restricoes: [] };
          grupos.push(grupo);
        }

        grupo.restricoes.push(restricao);

        return grupos;
      }, []);
    },

    algumaLinhaComVarios() {
      return this.grupos.some((grupo) => grupo.restricoes.length > 1);
    },
  },

  methods: {
    chaveRotulo,

    mapearOpcao: mapearOpcaoRestricao,

    descricaoDe(restricao) {
      return descricaoRestricao(restricao, this.rotulos);
    },

    trocarTipo(tipo) {
      this.nova.tipo = tipo;
      this.nova.chave = "";
    },

    escolherChave(valor, opcao) {
      const chave = valor === null || valor === undefined ? "" : String(valor).trim();

      this.nova.chave = chave;

      if (chave && opcao && opcao.descricao) this.$emit("rotulo", chaveRotulo(this.nova.tipo, chave), opcao.descricao);
    },

    adicionar() {
      if (!this.podeAdicionar) return;

      this.item.restricoes.push({ tipo: this.nova.tipo, chave: this.chaveDigitada });
      this.nova.chave = "";
    },

    remover(restricao) {
      const indice = this.item.restricoes.indexOf(restricao);

      if (indice >= 0) this.item.restricoes.splice(indice, 1);
    },
  },
};
</script>

<style scoped src="@/components/regras/regra-modal.css"></style>
<style scoped src="@/components/regras/regra-restricoes.css"></style>

<style scoped>
.regra-negocio-restricoes__descricao {
  font-size: 12px;
  color: #667085;
  margin-bottom: 12px;
}

@media (max-width: 767.98px) {
  .regra-restricoes__linha {
    grid-template-columns: 1fr;
    gap: 6px;
  }

  .regra-restricoes__conector-texto {
    left: 0;
  }
}
</style>
